import React, { createContext, useContext, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { ABOUT_ICONS, safeAboutUrl } from "../../utils/aboutContent";
import { parseBlocks } from "../../utils/aboutBlocks";
import EditableSection from "../../templates/Site1/components/EditableSection/EditableSection";

/**
 * The About section's content, as the public page shows it: one component per
 * block the About editor writes (utils/aboutBlocks.js), and anything that is not
 * a block — older content, hand-written HTML — rendered as it is, through the
 * same sanitized element-by-element walk it always had.
 *
 * Colours come from the page's `--site1-*` properties, with EasyPlanner's own
 * as the fallback outside a Site1 page.
 */

// Shared by the editor and the public renderer. Auto-fit lets two-column image
// layouts and three-card layouts use the same sanitized CMS class without an
// empty third column. A direct figure identifies an image-and-copy row.
export const aboutTypographyClass = [
  "[overflow-wrap:anywhere] text-base leading-relaxed text-ink-600",
  "[&_p]:mb-5 [&_h2]:mt-8 [&_h2]:mb-4 [&_h2]:text-fluid-xl [&_h2]:font-bold [&_h2]:text-ink-900",
  "[&_h3]:mt-5 [&_h3]:mb-3 [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:text-ink-900",
  "[&_ul]:mb-5 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:mb-5 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:mb-2",
  "[&_a]:text-brand-700 [&_a]:underline [&_a]:underline-offset-4 [&_a]:transition-colors hover:[&_a]:text-brand-900 focus-visible:[&_a]:outline-2 focus-visible:[&_a]:outline-offset-4",
  "[&_blockquote]:my-7 [&_blockquote]:border-l-4 [&_blockquote]:border-brand-300 [&_blockquote]:pl-6 [&_blockquote]:italic [&_hr]:my-8 [&_hr]:border-ink-200",
  "[&_img]:my-5 [&_img]:max-h-[32rem] [&_img]:w-full [&_img]:rounded-2xl [&_img]:object-contain [&_figcaption]:text-sm [&_figcaption]:text-ink-500",
  "[&_.ep-about-grid]:my-7 [&_.ep-about-grid]:grid [&_.ep-about-grid]:gap-5 sm:[&_.ep-about-grid]:grid-cols-2 lg:[&_.ep-about-grid]:grid-cols-[repeat(auto-fit,minmax(17.5rem,1fr))]",
  "[&_.ep-about-grid:has(>figure)]:my-12 [&_.ep-about-grid:has(>figure)]:items-center [&_.ep-about-grid:has(>figure)]:gap-8 [&_.ep-about-grid>figure]:min-w-0 [&_.ep-about-grid>figure_img]:shadow-card",
  "[&_.ep-about-card]:min-w-0 [&_.ep-about-card]:rounded-2xl [&_.ep-about-card]:border [&_.ep-about-card]:border-ink-200 [&_.ep-about-card]:bg-ink-50 [&_.ep-about-card]:p-6",
  "[&_.ep-about-card>h3:first-child]:mt-0 [&_.ep-about-card>p:last-child]:mb-0 [&_.ep-about-lead]:text-fluid-lg [&_.ep-about-lead]:text-ink-700",
  "[&>*:first-child]:mt-0 [&>*:last-child]:mb-0",
].join(" ");

/**
 * Set by Site1Template in Site & widget's preview while the About section is
 * the one being edited: each block becomes clickable, and the block being
 * edited is outlined. Absent everywhere else.
 */
export const AboutBlockEditorContext = createContext(null);

const headingColor = "text-[var(--site1-heading,var(--color-ink-900))]";
const blockHeadingClass = `m-0 text-[clamp(1.625rem,1.25rem+1.5vw,2.5rem)] leading-tight font-bold tracking-tight ${headingColor}`;
const solidButtonClass =
  "inline-flex min-h-12 items-center justify-center rounded-xl bg-[var(--site1-primary,var(--color-brand-600))] px-7 py-3 font-semibold text-[var(--site1-on-primary,#ffffff)] no-underline transition hover:bg-[var(--site1-primary-hover,var(--color-brand-700))] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--site1-primary-strong,var(--color-brand-700))] disabled:opacity-60";
const outlineButtonClass =
  "inline-flex min-h-12 items-center justify-center rounded-xl border-2 border-[var(--site1-primary,var(--color-brand-600))] bg-white px-7 py-3 font-semibold text-[var(--site1-primary-strong,var(--color-brand-700))] no-underline transition hover:bg-[var(--site1-primary-soft,var(--color-brand-50))] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--site1-primary-strong,var(--color-brand-700))]";

/** Sanitized HTML → React elements, one element at a time; never innerHTML. */
const renderHtml = (html, context, prefix = "n") => {
  const document = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");
  const { t, onBook, bookingLabel } = context;
  const renderNode = (node, key) => {
    if (node.nodeType === 3) return node.textContent;
    if (node.nodeType !== 1) return null;
    const tag = node.tagName.toLowerCase();
    if (tag === "div" && node.getAttribute("data-booking-widget") === "true") {
      return <div key={key} className="my-8 rounded-2xl border border-brand-200 bg-brand-50 p-6 sm:p-8">
        <div className="flex flex-wrap items-center justify-between gap-5">
          <div><i className="bi bi-calendar2-check mr-2 text-brand-600" aria-hidden="true" /><strong className="text-ink-900">{t("Dashboard.AboutEditor.widgetTitle")}</strong><p className="mt-2 mb-0 text-sm">{t(onBook ? "Dashboard.AboutEditor.widgetPublic" : "Dashboard.AboutEditor.widgetPreview")}</p></div>
          <button type="button" disabled={!onBook} onClick={onBook} className="min-h-11 rounded-xl bg-[var(--site1-primary,var(--color-brand-600))] px-6 py-3 font-semibold text-[var(--site1-on-primary,#ffffff)] hover:brightness-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 disabled:opacity-60">{bookingLabel || t("BookingPage.hero.book")}</button>
        </div>
      </div>;
    }
    const props = { key };
    for (const attribute of [...node.attributes]) props[attribute.name === "class" ? "className" : attribute.name] = attribute.value;
    if (tag === "a") { props.target = "_blank"; props.rel = "noopener noreferrer"; }
    if (tag === "img") { props.loading = "lazy"; props.decoding = "async"; props.alt = props.alt || ""; }
    return React.createElement(tag, props, ["img", "br", "hr"].includes(tag) ? undefined : [...node.childNodes].map((child, index) => renderNode(child, `${key}-${index}`)));
  };
  return [...document.body.childNodes].map((node, index) => renderNode(node, `${prefix}-${index}`));
};

/** Plain text written in a field: paragraphs at blank lines, line breaks kept. */
const Paragraphs = ({ text, className }) =>
  String(text || "")
    .split(/\n\s*\n/)
    .filter((part) => part.trim())
    .map((part, index) => (
      <p key={index} className={`${className} whitespace-pre-line`}>
        {part.trim()}
      </p>
    ));

const TextBlock = ({ block, context }) => {
  const center = block.align === "center";
  return (
    <div className={`mx-auto w-full max-w-3xl ${center ? "text-center" : ""}`}>
      {block.title && <h2 className={blockHeadingClass}>{block.title}</h2>}
      {block.body && (
        <div className={`${aboutTypographyClass} ${block.title ? "mt-5" : ""} ${center ? "[&_ol]:text-left [&_ul]:text-left" : ""}`}>
          {renderHtml(block.body, context)}
        </div>
      )}
    </div>
  );
};

const CardsBlock = ({ block }) => (
  <div>
    {(block.title || block.intro) && (
      <div className="mx-auto mb-8 max-w-2xl text-center [overflow-wrap:anywhere]">
        {block.title && <h2 className={blockHeadingClass}>{block.title}</h2>}
        <Paragraphs text={block.intro} className="mt-3 mb-0 text-fluid-lg leading-relaxed text-ink-600" />
      </div>
    )}
    <div className="grid gap-5 [grid-template-columns:repeat(auto-fit,minmax(min(15rem,100%),1fr))]">
      {block.cards.map((card, index) => (
        <div key={index} className="min-w-0 rounded-2xl border border-ink-100 bg-white p-6 shadow-card [overflow-wrap:anywhere]">
          {ABOUT_ICONS[card.icon] && (
            <span className="mb-4 flex size-12 items-center justify-center rounded-full bg-[var(--site1-primary-muted,var(--color-brand-100))] text-[var(--site1-primary-strong,var(--color-brand-700))]">
              <i className={`bi ${ABOUT_ICONS[card.icon]} text-xl`} aria-hidden="true" />
            </span>
          )}
          {card.title && <h3 className={`m-0 text-lg font-semibold ${headingColor}`}>{card.title}</h3>}
          <Paragraphs text={card.text} className="mt-2 mb-0 leading-relaxed text-ink-600" />
        </div>
      ))}
    </div>
  </div>
);

const MediaBlock = ({ block, context }) => {
  const src = block.image?.src ? safeAboutUrl(block.image.src, true) : null;
  return (
    <div className="overflow-hidden rounded-3xl bg-[var(--site1-primary-soft,var(--color-brand-50))] p-6 sm:p-8 md:p-10">
      <div className={`grid items-center gap-8 ${src ? "md:grid-cols-2" : ""}`}>
        {src && (
          <figure className={`m-0 min-w-0 ${block.side === "right" ? "md:order-2" : ""}`}>
            <img src={src} alt={block.image.alt || ""} loading="lazy" decoding="async" className="w-full rounded-2xl object-cover shadow-card" />
          </figure>
        )}
        <div className="min-w-0 [overflow-wrap:anywhere]">
          {block.eyebrow && (
            <p className="m-0 mb-3 text-xs font-bold tracking-[0.14em] text-[var(--site1-primary-strong,var(--color-brand-700))] uppercase">
              {block.eyebrow}
            </p>
          )}
          {block.title && <h2 className={blockHeadingClass}>{block.title}</h2>}
          {block.body && (
            <div className={`${aboutTypographyClass} ${block.title || block.eyebrow ? "mt-4" : ""}`}>{renderHtml(block.body, context)}</div>
          )}
        </div>
      </div>
    </div>
  );
};

const FaqBlock = ({ block }) => (
  <div className="mx-auto w-full max-w-3xl">
    {block.title && <h2 className={`${blockHeadingClass} mb-6 text-center`}>{block.title}</h2>}
    <div className="divide-y divide-ink-200 rounded-2xl border border-ink-200 bg-white">
      {block.items.map((item, index) => (
        <details key={index} className="group px-5 sm:px-6">
          <summary className="flex min-h-14 cursor-pointer list-none items-center justify-between gap-4 py-3 font-semibold text-ink-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--site1-primary-strong,var(--color-brand-700))] [&::-webkit-details-marker]:hidden">
            <span className="min-w-0 [overflow-wrap:anywhere]">{item.question}</span>
            <i className="bi bi-chevron-down shrink-0 text-[var(--site1-primary-strong,var(--color-brand-700))] transition-transform group-open:rotate-180 motion-reduce:transition-none" aria-hidden="true" />
          </summary>
          <div className="pb-5 [overflow-wrap:anywhere]">
            <Paragraphs text={item.answer} className="mt-0 mb-3 leading-relaxed text-ink-600 last:mb-0" />
          </div>
        </details>
      ))}
    </div>
  </div>
);

const LinkBlock = ({ block }) => {
  const href = block.href ? safeAboutUrl(block.href) : null;
  const className = block.style === "outline" ? outlineButtonClass : solidButtonClass;
  return (
    <div className={`flex ${block.align === "center" ? "justify-center" : "justify-start"}`}>
      {href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
          {block.text}
        </a>
      ) : (
        <span className={`${className} cursor-default opacity-60`}>{block.text}</span>
      )}
    </div>
  );
};

const BookingBlock = ({ block, context }) => {
  const { t, onBook, bookingLabel } = context;
  return (
    <div className="flex flex-col gap-6 rounded-3xl border border-[var(--site1-primary-muted,var(--color-brand-100))] bg-[var(--site1-primary-soft,var(--color-brand-50))] p-6 sm:p-8 md:flex-row md:items-center md:justify-between md:p-10">
      <div className="min-w-0 [overflow-wrap:anywhere]">
        <h2 className={`m-0 text-fluid-xl leading-tight font-bold ${headingColor}`}>
          {block.title || t("Dashboard.AboutEditor.widgetTitle")}
        </h2>
        {block.text ? (
          <Paragraphs text={block.text} className="mt-2 mb-0 leading-relaxed text-ink-600" />
        ) : (
          <p className="mt-2 mb-0 leading-relaxed text-ink-600">
            {t(onBook ? "Dashboard.AboutEditor.widgetPublic" : "Dashboard.AboutEditor.widgetPreview")}
          </p>
        )}
      </div>
      <button type="button" disabled={!onBook} onClick={onBook} className={`${solidButtonClass} shrink-0`}>
        {bookingLabel || t("BookingPage.hero.book")}
      </button>
    </div>
  );
};

const VIEWS = { text: TextBlock, cards: CardsBlock, media: MediaBlock, faq: FaqBlock, link: LinkBlock, booking: BookingBlock };

const blockPropTypes = { block: PropTypes.object.isRequired, context: PropTypes.object };
[TextBlock, CardsBlock, MediaBlock, FaqBlock, LinkBlock, BookingBlock].forEach((view) => {
  view.propTypes = blockPropTypes;
});
Paragraphs.propTypes = { text: PropTypes.string, className: PropTypes.string };

const AboutContent = ({ value, onBook, bookingLabel }) => {
  const { t } = useTranslation();
  const blockEditor = useContext(AboutBlockEditorContext);
  const blocks = useMemo(() => parseBlocks(value), [value]);
  const context = { t, onBook, bookingLabel };

  return (
    <div className="flex flex-col gap-14 md:gap-20">
      {blocks.map((block, index) => {
        const View = VIEWS[block.type];
        const content = View ? (
          <View block={block} context={context} />
        ) : (
          <div className={aboutTypographyClass}>{renderHtml(block.html, context, `b${index}`)}</div>
        );
        if (!blockEditor) return <React.Fragment key={index}>{content}</React.Fragment>;
        const label = t(`Dashboard.AboutEditor.blocks.${block.type}`);
        return (
          <div key={index} data-about-block={index} className="scroll-mt-24">
            <EditableSection
              section={`block-${index}`}
              editor={{
                active: blockEditor.activeBlock === index ? `block-${index}` : null,
                onSelect: () => blockEditor.onSelectBlock(index),
                labels: { [`block-${index}`]: label },
              }}
            >
              {content}
            </EditableSection>
          </div>
        );
      })}
    </div>
  );
};

AboutContent.propTypes = {
  value: PropTypes.string,
  onBook: PropTypes.func,
  bookingLabel: PropTypes.string,
};

export default AboutContent;
